import type { CaptureOptions, PostHog } from "posthog-js";
import { uuidv4 } from "uuidv7";

import { EventName, GoogleTagManagerOnlyEventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import posthog from "posthog-js";

const RECORD_EVENTS_ON_DEV = true;

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const flattenDictionary = (properties: { [key: string]: any }) => {
  // flatten a dictionary to a single level, does this recursively
  // e.g. {a: {b: 1}} => {"a__b": 1}
  // e.g. {a: {b: {c: 1}}} => {"a__b__c": 1}
  // e.g. {a: 1, b: {c: 1}} => {"a": 1, "b__c": 1}
  // e.g. {a: [1, 2, 3]} => {"a": [1, 2, 3]}
  const flattenedProperties: { [key: string]: any } = {};

  for (const key in properties) {
    const value = properties[key];
    if (typeof value === "object" && !Array.isArray(value)) {
      const flattenedValue = flattenDictionary(value);
      for (const flattenedKey in flattenedValue) {
        flattenedProperties[`${key}__${flattenedKey}`] = flattenedValue[flattenedKey];
      }
    } else {
      flattenedProperties[key] = value;
    }
  }

  return flattenedProperties;
};
export const captureEvent = (event: EventName, properties?: { [key: string]: any }, options?: CaptureOptions) => {
  return _captureEvent(posthog, event, properties, options);
};

export const _captureEvent = (
  posthog: PostHog,
  event: EventName,
  properties?: { [key: string]: any },
  options?: CaptureOptions,
) => {
  try {
    const eventName = EventName[event];
    if (typeof window === "undefined") {
      logger.warn("window is not defined. Unable to capture event with PostHog.");
      return;
    }
    if (process.env.NODE_ENV === "development" && !RECORD_EVENTS_ON_DEV) {
      const flat_properties = { ...flattenDictionary(properties || {}) };
      logger.info(`Not sending event as on local env. Event ${eventName}`, flat_properties);
      return;
    }

    const event_id = uuidv4();
    const flat_properties = { ...flattenDictionary(properties || {}), event_id };

    if (properties) {
      const posthogProperties: Record<string, any> = {
        ...flat_properties,
      };

      const set_properties = {
        ...(properties["$set"] || {}),
      };
      const set_once_properties = properties["$set_once"] || undefined;
      if (set_properties) {
        posthogProperties["$set"] = set_properties;
      }
      if (set_once_properties) {
        posthogProperties["$set_once"] = set_once_properties;
      }
      posthog?.capture(eventName.toString(), posthogProperties, options);
    } else {
      posthog?.capture(eventName.toString(), { event_id }, options);
    }

    window.dataLayer = window.dataLayer || [];

    // log to Google Tag Manager
    const eventPropsForDataLayer = properties ? { ...properties, event_id } : { event_id };
    window.dataLayer?.push({
      event: eventName.toString(),
      eventProps: eventPropsForDataLayer,
      event_id,
    });
  } catch (e) {
    logger.error(e instanceof Error ? e : null, "Error capturing event");
  }
};

// identify handled by posthog in XI and in a cross sub-domain cookie that posthog automatically uses

export const pushToDataLayer = (event: GoogleTagManagerOnlyEventName, properties?: { [key: string]: any }) => {
  const eventName = GoogleTagManagerOnlyEventName[event];
  window.dataLayer = window.dataLayer || [];
  window.dataLayer?.push({
    event: eventName.toString(),
    ...properties,
  });
};
